/**
 * Арифметика просмотра фото: масштаб и сдвиг картинки. Без DOM, поэтому проверяется тестом отдельно от жестов.
 *
 * Картинка лежит по центру сцены, `transform-origin: center`; сдвиг `x, y` — в пикселях экрана от центра.
 */
export interface PhotoView {
  scale: number
  x: number
  y: number
}

export const MIN_SCALE = 1
export const MAX_SCALE = 6
/** Шаг кнопок «+» / «−» и колеса мыши (множитель). */
export const BUTTON_STEP = 1.5
/** Масштаб двойного тапа из положения «по размеру». */
export const DOUBLE_TAP_SCALE = 2.5

export const FIT: PhotoView = { scale: MIN_SCALE, x: 0, y: 0 }

export const clampScale = (scale: number): number => Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale))

/** Сдвиг не выводит край картинки за край видимой области: размер — показанный (при масштабе 1) размер картинки. */
export function clampView(view: PhotoView, width: number, height: number): PhotoView {
  const scale = clampScale(view.scale)
  if (scale <= MIN_SCALE) return FIT
  const limitX = (width * (scale - 1)) / 2
  const limitY = (height * (scale - 1)) / 2
  return { scale, x: Math.min(limitX, Math.max(-limitX, view.x)), y: Math.min(limitY, Math.max(-limitY, view.y)) }
}

/** Новый масштаб так, чтобы точка под пальцем `(px, py)` (от центра сцены) осталась на месте. */
export function zoomAt(view: PhotoView, nextScale: number, px: number, py: number, width: number, height: number): PhotoView {
  const scale = clampScale(nextScale)
  const ratio = scale / view.scale
  return clampView({ scale, x: px - (px - view.x) * ratio, y: py - (py - view.y) * ratio }, width, height)
}

/** Двойной тап: из «по размеру» — приблизить, из любого приближения — вернуть по размеру. */
export function toggleZoomAt(view: PhotoView, px: number, py: number, width: number, height: number): PhotoView {
  return view.scale > MIN_SCALE ? FIT : zoomAt(view, DOUBLE_TAP_SCALE, px, py, width, height)
}

export const percentText = (scale: number): string => `${Math.round(scale * 100)} %`
